import Button from '@/features/shared/Button';
import Image from '@/features/shared/Image';
import {
  MuiButtonBase,
  MuiCard,
  MuiCardActions,
  MuiCardContent,
  MuiCardMedia,
  MuiTypography,
} from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import TwoColumnContent from '@/features/shared/TwoColumnContent';
import { getPageUrl, mapLinkToProps } from '@/utils/helpers';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import React, { memo } from 'react';
import useStyles from './ColumnCard.styles';

const FeaturedCard = ({
  cardImage,
  cardDescription,
  cardHeading,
  cardCategory,
  cardPublicationDate,
  cardReadTime,
  cardAuthor,
  theme,
  buttonLink,
  cardImageType = 'base',
  className: overrideClassName,
  url = '',
}: any) => {
  const classes = useStyles({});
  const cardClassName = clsx(classes.card, `${theme}-theme`, overrideClassName);
  const cardLinkProps = mapLinkToProps({ href: url });
  const hasCardUrl = !!url;

  return (
    <div className={cardClassName}>
      <TwoColumnContent
        columnRatio="65-35"
        className={classes.columns}
        leftColumn={
          cardImage && (
            <MuiCardMedia className={classes.image}>
              <Image
                image={{ ...cardImage, fullWidthImage: true }}
                type={cardImageType}
                roundedCorners={false}
              />
            </MuiCardMedia>
          )
        }
        rightColumn={
          <MuiCardContent className={clsx(classes.featuredContent, 'gtm-headline-container')}>
            {cardCategory?.length > 0 && (
              <MuiTypography variant="overline" component="div" className={classes.category}>
                {cardCategory}
              </MuiTypography>
            )}
            {cardHeading && (
              <MuiTypography
                className={clsx(classes.contentHeading, 'gtm-headline')}
                variant="h4"
                dangerouslySetInnerHTML={{ __html: cardHeading }}
              />
            )}
            <CaptureClicksRichText
              theme={theme}
              containerProps={{ className: classes.cardDescription }}
              content={cardDescription}
            />
            {buttonLink?.displayText && (
              <MuiCardActions className={classes.actions}>
                <Button
                  link={buttonLink}
                  theme={theme}
                  className={clsx(classes.cardActionsLink, 'gtm-featured-content-cta')}
                />
              </MuiCardActions>
            )}
            {(cardReadTime || cardPublicationDate || cardAuthor?.name) && (
              <MuiTypography variant="caption" component="div" className={classes.date}>
                {cardPublicationDate && <span>{cardPublicationDate}</span>}
                {cardPublicationDate && cardReadTime && <span className={classes.divider}>|</span>}
                {cardReadTime && <span>{cardReadTime}</span>}
                {cardAuthor && <p>{cardAuthor?.name}</p>}
              </MuiTypography>
            )}
          </MuiCardContent>
        }
        theme={theme}
      />
      {hasCardUrl && (
        <MuiButtonBase
          {...cardLinkProps}
          className={clsx(classes.cardLink, 'gtm-featured-content-cta')}
        />
      )}
    </div>
  );
};

const DefaultCard = ({
  cardImage,
  cardDescription,
  cardHeading,
  cardCategory,
  cardPublicationDate,
  cardReadTime,
  cardAuthor,
  cardColor,
  theme,
  cardTheme,
  buttonLink,
  hideImage,
  className: overrideClassName,
  url = '',
}: any) => {
  const classes = useStyles({});
  const cardClassName = clsx(
    classes.card,
    `${theme}-theme`,
    `bg-color-${cardColor}`,
    overrideClassName,
    'gtm-headline-container',
    'gntc-default-card',
  );
  const cardLinkProps = mapLinkToProps({ href: url });
  const hasCardUrl = !!url;

  return (
    <MuiCard className={cardClassName} elevation={8} data-testid="qa-column-component">
      <TwoColumnContent
        className={classes.defaultColumns}
        leftColumn={
          !hideImage &&
          cardImage && (
            <MuiCardMedia className={clsx(classes.image, classes.defaultImage)}>
              <Image image={cardImage} type="card" size="cover" roundedCorners={false} />
            </MuiCardMedia>
          )
        }
        rightColumn={
          <MuiCardContent className={classes.defaultContent}>
            {cardCategory?.length > 0 && (
              <MuiTypography variant="overline" component="div" className={classes.category}>
                {cardCategory}
              </MuiTypography>
            )}
            {cardHeading && (
              <MuiTypography
                className={clsx(classes.contentHeading, 'gtm-headline')}
                variant="h6"
                dangerouslySetInnerHTML={{ __html: cardHeading }}
              />
            )}
            {cardDescription && (
              <CaptureClicksRichText
                theme={cardTheme}
                containerProps={{ className: classes.cardDescription }}
                content={cardDescription}
              />
            )}
            {buttonLink?.displayText && (
              <MuiCardActions className={classes.actions}>
                <Button
                  link={buttonLink}
                  theme={cardTheme}
                  className={clsx(classes.cardActionsLink, 'gtm-featured-content-cta')}
                />
              </MuiCardActions>
            )}
            {(cardReadTime || cardPublicationDate || cardAuthor?.name) && (
              <MuiTypography variant="caption" component="div" className={classes.date}>
                {cardPublicationDate && <span>{cardPublicationDate}</span>}
                {cardPublicationDate && cardReadTime && <span className={classes.divider}>|</span>}
                {cardReadTime && <span>{cardReadTime}</span>}
                {cardAuthor && <p>{cardAuthor?.name}</p>}
              </MuiTypography>
            )}
          </MuiCardContent>
        }
      />
      {hasCardUrl && (
        <MuiButtonBase
          {...cardLinkProps}
          className={clsx(classes.cardLink, 'gtm-featured-content-cta')}
        />
      )}
    </MuiCard>
  );
};

const ColumnCard = ({ cardColor, theme, cardTheme, isFeaturedContent = false, ...props }: any) => {
  const page = React.useContext(BrPageContext);
  const url = page && getPageUrl({ url: props?.url, page });

  const cardProps = {
    ...props,
    cardColor,
    theme,
    cardTheme,
    url,
  };

  return !isFeaturedContent ? <DefaultCard {...cardProps} /> : <FeaturedCard {...cardProps} />;
};

export default memo(ColumnCard);
